import { Box } from "@chakra-ui/react";
import { colors, customEvent, getCdnImageSrc, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  MubinsoCheckItem,
  MubinsoCtaButton,
  MubinsoInfoBox,
  MubinsoScreenLayout,
} from "../Mubinso/MubinsoUI";
import { MubinsoReview } from "../Mubinso/MubinsoConversionScreen";
import { MubinsoLeadModal } from "../Mubinso/MubinsoLeadModal";
import { MubinsoScreenChromeContext } from "../Mubinso/mubinsoScreenChromeContext";
import { mubinsoLeadStatus } from "../Mubinso/mubinsoConstants";
import { buildCrematoriumLeadMessage } from "./crematoriumLeadMessage";
import {
  CREMA_CONVERSION,
  CREMA_FUNNEL_STEP1,
  CREMA_FUNNEL_STEP2,
  CREMA_FUNNEL_STEP3,
} from "./crematoriumConstants";

const TOTAL_INFO_STEPS = 3;

/**
 * 화장장 퍼널 정보 스텝 (3스텝 정보 + 전환 화면).
 * 스텝 1~3: 화장장 예약 대란 → 추가금 → 장례지도사
 * 스텝 4: 전환 화면 (리드 모달 열기)
 */
export function CrematoriumInfoFunnel({
  q1Answer,
  q2Answer,
  q3Answer,
  onComplete,
}: {
  q1Answer: string;
  q2Answer: string;
  q3Answer: string;
  onComplete: () => void;
}) {
  const [step, setStep] = useState(1);
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleNext = useCallback(() => {
    customEvent({
      newGtm: { path: "crema_funnel", trigger_and_target: `funnel-next-${step}` },
    });
    setStep((s) => s + 1);
  }, [step]);

  const messageBuilder = useCallback(
    () => buildCrematoriumLeadMessage({ q1Answer, q2Answer, q3Answer }),
    [q1Answer, q2Answer, q3Answer],
  );

  if (step <= TOTAL_INFO_STEPS) {
    return <InfoStep step={step} onNext={handleNext} />;
  }

  // 스텝 4: 전환 화면
  return (
    <>
      <CrematoriumConversionScreen
        onPrimary={() => {
          customEvent({
            newGtm: { path: "crema_funnel", trigger_and_target: "conv-cta" },
          });
          setLeadOpen(true);
        }}
      />
      <MubinsoLeadModal
        isOpen={leadOpen}
        onClose={() => setLeadOpen(false)}
        title="화장장 안내 신청"
        freeTag
        description={"번호를 남겨주시면\n장례지도사가 직접 안내해드려요."}
        buttonText="무료 안내 신청하기"
        eventPath="crema_funnel"
        leadStatus={mubinsoLeadStatus(q2Answer)}
        messageBuilder={messageBuilder}
        onSubmitted={onComplete}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 정보 스텝 1~3
// ---------------------------------------------------------------------------
function InfoStep({ step, onNext }: { step: number; onNext: () => void }) {
  return (
    <MubinsoScreenLayout
      progress={{ current: step, total: TOTAL_INFO_STEPS }}
      footer={<MubinsoCtaButton label="다음" onClick={onNext} />}
    >
      {step === 1 && <Step1Content />}
      {step === 2 && <Step2Content />}
      {step === 3 && <Step3Content />}
    </MubinsoScreenLayout>
  );
}

function Step1Content() {
  const c = CREMA_FUNNEL_STEP1;
  return (
    <>
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        <HighlightedHeadline text={c.headline} highlight={c.highlightText} />
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {c.body}
      </Box>
      <Spacing gap={20} />
      <MubinsoInfoBox variant="peach">
        <span dangerouslySetInnerHTML={{ __html: c.callout }} />
      </MubinsoInfoBox>
    </>
  );
}

function Step2Content() {
  const c = CREMA_FUNNEL_STEP2;
  return (
    <>
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        <HighlightedHeadline text={c.headline} highlight={c.highlightText} />
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {c.body}
      </Box>
      <Spacing gap={20} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={getCdnImageSrc(c.image)}
        alt={c.imageAlt}
        style={{ width: "100%", display: "block", borderRadius: "16px" }}
      />
    </>
  );
}

function Step3Content() {
  const c = CREMA_FUNNEL_STEP3;
  return (
    <>
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        <HighlightedHeadline text={c.headline} highlight={c.highlightText} />
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {c.body}
      </Box>
      <Spacing gap={20} />
      <BaseStack width="100%" spacing={12}>
        {c.checks.map((text) => (
          <MubinsoCheckItem key={text}>{text}</MubinsoCheckItem>
        ))}
      </BaseStack>
      <Spacing gap={20} />
      <MubinsoInfoBox variant="peach">
        <span dangerouslySetInnerHTML={{ __html: c.callout }} />
      </MubinsoInfoBox>
    </>
  );
}

// ---------------------------------------------------------------------------
// 전환 화면 (MubinsoConversionScreen 레이아웃 재사용하되 이벤트 별도 처리)
// ---------------------------------------------------------------------------
function CrematoriumConversionScreen({ onPrimary }: { onPrimary: () => void }) {
  const chrome = useContext(MubinsoScreenChromeContext);

  // footer 숨김 + 최상단 스크롤
  useEffect(() => {
    chrome?.setHideFooter(true);
    return () => chrome?.setHideFooter(false);
  }, [chrome]);

  useEffect(() => {
    const t = setTimeout(() => window.scrollTo(0, 0), 0);
    return () => clearTimeout(t);
  }, []);

  const c = CREMA_CONVERSION;
  return (
    <MubinsoScreenLayout
      footer={<MubinsoCtaButton label={c.primaryLabel} onClick={onPrimary} showChevron={false} />}
    >
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        <HighlightedHeadline text={c.headline} highlight={c.highlightText} />
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {c.sub}
      </Box>

      <Spacing gap={24} />
      <BaseStack width="100%" spacing={12}>
        {c.checkItems.map((item) => (
          <MubinsoCheckItem key={item.text} alignStart>
            {item.text}
          </MubinsoCheckItem>
        ))}
      </BaseStack>

      <Spacing gap={28} />
      <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20} spacing={14}>
        <Box className="subtitle_14_b gray900">{c.reviewTitle}</Box>
        <BaseStack width="100%" spacing={10}>
          {c.reviews.map((review: MubinsoReview, i: number) => (
            <BaseStack
              key={i}
              width="100%"
              bg={colors.white}
              borderRadius="12px"
              p={16}
              spacing={8}
            >
              <Box className="body_14_r gray800" whiteSpace="pre-line">
                {review.text}
              </Box>
              <Box className="caption_12_b" color={colors.gray400}>
                {review.meta}
              </Box>
            </BaseStack>
          ))}
        </BaseStack>
      </BaseStack>
    </MubinsoScreenLayout>
  );
}

// ---------------------------------------------------------------------------
// headline 내 특정 텍스트를 오렌지로 강조
// ---------------------------------------------------------------------------
function HighlightedHeadline({ text, highlight }: { text: string; highlight: string }) {
  const parts = text.split(highlight);
  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 && <span style={{ color: colors.orange650 }}>{highlight}</span>}
        </span>
      ))}
    </>
  );
}
